import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { camelCase } from 'lodash';
import { connect } from 'react-redux';
import { copyTasks } from 'bundles/apex/playbooks/config/store/playbooks/actions';

import { Button } from 'components/ui/button';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import ManagedInput from 'components/form/managed_input';
import Modal from 'components/modal';
import Selects from './selects';
import TasksTable from './table';
import { Errors } from 'components/ui/errors';
import { getOrderedStages } from 'bundles/apex/helpers/stages';
import { getPlaybookTasks, getPlaybookType } from 'bundles/apex/playbooks/config/helpers';

export function CopyTaskModal({
  copyTasks,
  playbooks,
  playbookRecordType,
  playbookType,
  playbookTypeOptions,
  show,
  stage,
  stages,
  status,
  statusOptions,
  onHide,
}) {
  const orderedStages = getOrderedStages(stages);
  const forDealPlaybook = playbookRecordType === 'deal';

  const [errors, setErrors] = useState();
  const [saving, setSaving] = useState();
  const [fromPlaybookType, setFromPlaybookType] = useState(playbookType);
  const [fromStage, setFromStage] = useState(stage);
  const [fromStatus, setFromStatus] = useState(status);
  const [toPlaybookType, setToPlaybookType] = useState(playbookTypeOptions[0].value);
  const [toStage, setToStage] = useState(orderedStages[0]);
  const [toStatus, setToStatus] = useState(statusOptions[0]);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [createTasks, setCreateTasks] = useState();

  const getTasks = (playbookType, target) => {
    const playbook = playbooks.find(playbook => getPlaybookType(playbook) === playbookType);

    if (playbook) {
      return getPlaybookTasks(playbook)[camelCase(target.key)] || [];
    }

    return [];
  };

  const tasks = useMemo(
    () => getTasks(fromPlaybookType, forDealPlaybook ? fromStage : fromStatus),
    [fromPlaybookType, fromStage, fromStatus, playbooks]
  );

  const reset = () => {
    const newTasks = getTasks(playbookType, stage);
    setErrors();
    setSaving();
    setFromPlaybookType(playbookType);
    setFromStage(stage);
    setToPlaybookType(newTasks.length === 0 ? playbookType : playbookTypeOptions[0].value);
    setToStage(newTasks.length === 0 ? stage : orderedStages[0]);
    setSelectedTaskIds([]);
    setCreateTasks();
  };

  const handleFromPlaybookTypeChange = (playbookType) => {
    setFromPlaybookType(playbookType);
    setSelectedTaskIds([]);
  };

  const handleFromTargetChange = (target) => {
    if (forDealPlaybook) {
      setFromStage(target);
    } else {
      setFromStatus(target);
    }
    setSelectedTaskIds([]);
  };

  const handleSelectAll = () => {
    if (selectedTaskIds.length === 0) {
      setSelectedTaskIds(tasks.map(task => task.id));
    } else {
      setSelectedTaskIds([]);
    }
  };

  const handleSelectTask = (task) => {
    if (selectedTaskIds.indexOf(task.id) === -1) {
      setSelectedTaskIds([...selectedTaskIds, task.id]);
    } else {
      setSelectedTaskIds(selectedTaskIds.filter(id => id !== task.id));
    }
  };

  async function handleSave() {
    setSaving(true);

    try {
      await copyTasks(
        tasks.filter(task => selectedTaskIds.indexOf(task.id) !== -1),
        toPlaybookType,
        (forDealPlaybook ? toStage : toStatus).key,
        createTasks,
        // A copied task leaves the selection, so Copy again after a failure sends only the failed ones (BSD-39275).
        copied => setSelectedTaskIds(ids => ids.filter(id => id !== copied.id))
      );
      onHide();
    } catch (errors) {
      setErrors(errors);
      setSaving(false);
    }
  }

  return (
    <Modal show={show} size="lg" onHide={onHide} onShow={reset}>
      <Modal.Header closeButton>Copy Tasks</Modal.Header>

      <Modal.Body>
        <Errors className="mb-3" errors={errors}/>

        <Selects
          label="Copy from"
          playbookRecordType={playbookRecordType}
          playbookType={fromPlaybookType}
          playbookTypeOptions={playbookTypeOptions}
          stage={fromStage}
          stages={stages}
          status={fromStatus}
          statusOptions={statusOptions}
          onPlaybookTypeChange={handleFromPlaybookTypeChange}
          onTargetChange={handleFromTargetChange}
        />

        {tasks.length > 0 ? (
          <React.Fragment>
            <TasksTable
              selectedTaskIds={selectedTaskIds}
              tasks={tasks}
              onSelectAll={handleSelectAll}
              onSelectTask={handleSelectTask}
            />
          </React.Fragment>
        ) :
          <Empty className="bg-white">
            <Empty.Content>{`
              No tasks exist for this ${playbookRecordType} type
              and ${playbookRecordType === 'deal' ? 'stage' : 'status'}.
            `}</Empty.Content>
          </Empty>
        }

        <Selects
          label="Copy to"
          playbookRecordType={playbookRecordType}
          playbookType={toPlaybookType}
          playbookTypeOptions={playbookTypeOptions}
          stage={toStage}
          stages={stages}
          status={toStatus}
          statusOptions={statusOptions}
          onPlaybookTypeChange={setToPlaybookType}
          onTargetChange={forDealPlaybook ? setToStage : setToStatus}
        />

        <ManagedInput
          name="createTasks"
          type="checkbox"
          value={createTasks}
          wrapperClassName="mt-3"
          onValueChange={setCreateTasks}
        >
          Add task to existing deals once copied
        </ManagedInput>
      </Modal.Body>

      <Modal.Footer>
        <Button name="cancel" variant="ghost" onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={selectedTaskIds.length === 0 || saving} name="save" onClick={handleSave}>
          Copy
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

CopyTaskModal.propTypes = {
  copyTasks: PropTypes.func.isRequired,
  playbookRecordType: PropTypes.string.isRequired,
  playbooks: PropTypes.array.isRequired,
  playbookType: PropTypes.string.isRequired,
  playbookTypeOptions: PropTypes.array.isRequired,
  show: PropTypes.bool,
  stage: PropTypes.shape({
    key: PropTypes.string.isRequired,
  }).isRequired,
  stages: PropTypes.object.isRequired,
  status: PropTypes.shape({
    key: PropTypes.string.isRequired,
  }).isRequired,
  statusOptions: PropTypes.array.isRequired,
  onHide: PropTypes.func.isRequired,
};

const mapStateToProps = ({
  playbookRecordType,
  playbookType,
  playbookTypeOptions,
  playbooks,
  stage,
  stages,
  status,
  statusOptions,
}) => {
  return {
    playbookRecordType,
    playbookType,
    playbookTypeOptions,
    playbooks,
    stage,
    stages,
    status,
    statusOptions,
  };
};

const mapDispatchToProps = {
  copyTasks
};

export default connect(mapStateToProps, mapDispatchToProps)(CopyTaskModal);
